import {OAuthPopupHandler} from "./AuthPopUp";
import {AuthUtils} from "../../utils/AuthUtils";

export type FhirProvider = "epic" | "charm" | "athena" | "cerner";

export async function startFhirAuth(
    provider: FhirProvider,
    opts: { endPoint?: string, executionId? : string, tabName? : string } = {}
) {
    const popup = new OAuthPopupHandler();

    const cfg = {
        epic: async () => {
            if (!opts.endPoint) throw new Error("epicEndpoint required");

            const codeVerifier  = AuthUtils.generateCodeVerifier();
            const codeChallenge = await AuthUtils.generateCodeChallenge(codeVerifier);
            sessionStorage.setItem("pkce_verifier", codeVerifier);

            const clientId = process.env.REACT_APP_EPIC_CLIENT_ID!;
            const redirect = `${process.env.REACT_APP_URL}/fetch/fhir`;
            let baseUrl  = opts.endPoint;
            // baseUrl = 'https://fhir.epic.com/interconnect-fhir-oauth/api/FHIR/R4'

            const auth   = await getAuthEndpoint(baseUrl);
            auth.searchParams.set("client_id", clientId);
            auth.searchParams.set("redirect_uri", redirect);
            auth.searchParams.set("response_type", "code");
            auth.searchParams.set("aud", baseUrl);
            auth.searchParams.set("scope", "launch/patient patient/*.read");
            auth.searchParams.set("state", "epic");
            auth.searchParams.set("code_challenge", codeChallenge);
            auth.searchParams.set("code_challenge_method", "S256");

            sessionStorage.setItem("client_id", clientId);
            sessionStorage.setItem("fhir_base_url", baseUrl);
            if(opts.executionId) sessionStorage.setItem("execution_id", opts.executionId);
            if(opts.tabName) sessionStorage.setItem("tab_name", opts.tabName);
            sessionStorage.setItem("fhir_base_url", baseUrl);
            sessionStorage.setItem("redirect_url", redirect);
            return auth.toString();
        },

        charm: async () => {
            const clientId = process.env.REACT_APP_CHARM_CLIENT_ID!;
            const redirect = `${process.env.REACT_APP_URL}/fetch/fhir`;
            const baseUrl  = "https://ehr.charmtracker.com/api/ehr/v2/fhir";

            const auth = await getAuthEndpoint(baseUrl);
            auth.searchParams.set("client_id", clientId);
            auth.searchParams.set("redirect_uri", redirect);
            auth.searchParams.set("response_type", "code");
            auth.searchParams.set("scope", "launch/patient patient/*.read");
            auth.searchParams.set("state", "charm");

            if(opts.executionId) sessionStorage.setItem("execution_id", opts.executionId);
            if(opts.tabName) sessionStorage.setItem("tab_name", opts.tabName);
            sessionStorage.setItem("client_id", clientId);
            sessionStorage.setItem("fhir_base_url", baseUrl);
            sessionStorage.setItem("redirect_url", redirect);

            return auth.toString();
        },
        athena: async () => {

            const codeVerifier  = AuthUtils.generateCodeVerifier();
            const codeChallenge = await AuthUtils.generateCodeChallenge(codeVerifier);
            sessionStorage.setItem("pkce_verifier", codeVerifier);

            const clientId = process.env.REACT_APP_ATHENA_CLIENT_ID!;
            const redirect = `${process.env.REACT_APP_URL}/fetch/fhir`;
            let baseUrl  = opts.endPoint;
            baseUrl = 'https://api.preview.platform.athenahealth.com/fhir/r4'

            const auth   = await getAuthEndpoint(baseUrl);
            auth.searchParams.set("client_id", clientId);
            auth.searchParams.set("redirect_uri", redirect);
            auth.searchParams.set("response_type", "code");
            auth.searchParams.set("aud", baseUrl);
            auth.searchParams.set("scope", "launch/patient patient/*.read");
            auth.searchParams.set("state", "athena");
            auth.searchParams.set("code_challenge", codeChallenge);
            auth.searchParams.set("code_challenge_method", "S256");

            sessionStorage.setItem("client_id", clientId);
            sessionStorage.setItem("fhir_base_url", baseUrl);
            if(opts.executionId) sessionStorage.setItem("execution_id", opts.executionId);
            if(opts.tabName) sessionStorage.setItem("tab_name", opts.tabName);
            sessionStorage.setItem("fhir_base_url", baseUrl);
            sessionStorage.setItem("redirect_url", redirect);
            return auth.toString();

        },
        cerner: async () => {
            if (!opts.endPoint) throw new Error("Cerner endpoint required");
            const codeVerifier  = AuthUtils.generateCodeVerifier();
            // const codeChallenge = await AuthUtils.generateCodeChallenge(codeVerifier);
            // sessionStorage.setItem("pkce_verifier", codeVerifier);

            // const clientId = process.env.REACT_APP_ATHENA_CLIENT_ID!;
            const clientId = '2c25b311-9ed5-45d5-bc86-cb68a653a2c2'
            const redirect = `${process.env.REACT_APP_URL}/fetch/fhir`;
            let baseUrl  = opts.endPoint;
            console.log('baseUrl')
            console.log(baseUrl)
            // baseUrl = 'https://fhir-myrecord.cerner.com/r4/ec2458f2-1e24-41c8-b71b-0e701af7583d'

            const auth   = await getAuthEndpoint(baseUrl);
            auth.searchParams.set("client_id", clientId);
            auth.searchParams.set("redirect_uri", redirect);
            auth.searchParams.set("response_type", "code");
            auth.searchParams.set("aud", baseUrl);
            auth.searchParams.set("scope", "launch/patient patient/Patient.read patient/Condition.read patient/Encounter.read patient/DocumentReference.read patient/Binary.read patient/Medication.read patient/MedicationRequest.read patient/MedicationStatement.read patient/Observation.read");
            auth.searchParams.set("state", "cerner");
            // auth.searchParams.set("code_challenge", codeChallenge);
            // auth.searchParams.set("code_challenge_method", "S256");

            sessionStorage.setItem("client_id", clientId);
            sessionStorage.setItem("fhir_base_url", baseUrl);
            if(opts.executionId) sessionStorage.setItem("execution_id", opts.executionId);
            if(opts.tabName) sessionStorage.setItem("tab_name", opts.tabName);
            sessionStorage.setItem("fhir_base_url", baseUrl);
            sessionStorage.setItem("redirect_url", redirect);
            return auth.toString();
        },
    };

    const url = await cfg[provider]();

    const r = await popup.openAuthPopup(url, { width: 700, height: 800 });
    if (!r.success) throw new Error(r.error_description || r.error);

    return { provider, code: r.code!, state: r.state! };
}

async function getAuthEndpoint(base: string): Promise<URL> {
    if (!base.endsWith("/")) base += "/";
    const res = await fetch(`${base}.well-known/smart-configuration`);
    const json = await res.json();
    return new URL(json.authorization_endpoint);
}
